/**
 * Start page (2026 redesign).
 *
 * PUBLIC — anyone can view. Signed-out visitors get a "sign in →" pill in the
 * top-right; signed-in users see their avatar + role. The top bar is unchanged
 * from the previous homepage (it still uses the classes in HomePage.css).
 *
 * Layout, top → bottom:
 *   A. Top bar (DesktopNav + user/sign-in)      — unchanged
 *   B. Wordmark + chicken (search bar removed)
 *   C. Quotes chyron (full-width scrolling ticker)
 *   D. Two columns: Weather / Tracks / Funny | Starship launch panel
 *   E. Banner grid (desktop 2×2): Everything is Stupid | Money
 *                                 Photos               | Jams
 *   F. Utilities (juggling chicken + app icons)
 *   G. Footer (Claude avatar + tagline)
 *
 * Data: current track, current comic, all quotes, and the /api/launches
 * proxy. Every fetch fails silent — each section has a static fallback.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.jsx';
import DesktopNav from '../components/DesktopNav.jsx';
import WeatherCard from '../components/WeatherCard.jsx';
import { getCurrentTrack } from '../api/tracks.js';
import { getCurrentComic } from '../api/comics.js';
import { listQuotes } from '../api/quotes.js';
import { getLaunches } from '../api/launches.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import CountdownClock, { countdownParts, shortDate } from '../components/CountdownClock.jsx';
import './HomePage.css';            // top-bar styles (unchanged)
import './homepage-redesign.css';   // everything below the top bar


// ─── utility icons (F) ─────────────────────────────────────────────
// 2 left, 2 right of the juggling chicken. NachoMovies is prepared but
// commented out in the JSX below until its frontend is ready.
const UTILS_LEFT = [
  { label: 'Nextcloud', url: 'https://nextcloud.nachofiles.com', icon: 'icon-nextcloud.png' },
  { label: 'Plex',      url: 'https://plex.nachofiles.com',      icon: 'icon-plex.png' },
];
const UTILS_RIGHT = [
  { label: 'Immich',  url: 'https://immich.nachofiles.com',  icon: 'icon-immich.png' },
  { label: 'TikTaco', url: 'https://tiktaco.nachofiles.com', icon: 'icon-tiktaco.png' },
];


export default function HomePage() {
  usePageTitle('');

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Current Tasty Track (null → fallback copy)
  const [currentTrack, setCurrentTrack] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getCurrentTrack()
      .then((t) => { if (!cancelled) setCurrentTrack(t); })
      .catch(() => { /* silent: card shows fallback */ });
    return () => { cancelled = true; };
  }, []);

  // Current comic (the /current endpoint wraps it as { current })
  const [currentComic, setCurrentComic] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getCurrentComic()
      .then((data) => { if (!cancelled) setCurrentComic(data?.current ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // All quotes for the chyron
  const [quotes, setQuotes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listQuotes()
      .then((data) => { if (!cancelled) setQuotes(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Launches (Starship hero + upcoming list)
  const [starship, setStarship] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getLaunches()
      .then((data) => {
        if (cancelled) return;
        setStarship(data?.starship ?? null);
        setUpcoming(Array.isArray(data?.upcoming) ? data.upcoming : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Live clock — drives the countdown, ticks once per second.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const cd = starship ? countdownParts(starship.net, nowMs) : null;
  const userInitials = user ? user.display_name.slice(0, 2).toUpperCase() : '';

  return (
    <div className="home-page dot-grid hr-home">
      {/* ── A. Top bar (unchanged) ── */}
      <header className="home-top">
        <DesktopNav />

        <div className="home-user">
          {user ? (
            <>
              {user.role === 'admin' && (
                <Link to="/admin" className="home-admin-link">admin →</Link>
              )}
              <Link to="/profile" className="avatar-link">
                <div className="avatar">
                  {user.avatar_url
                    ? <img src={`${user.avatar_url}?v=${Date.now()}`} alt="" className="mnav-avatar-img" />
                    : userInitials
                  }
                </div>
              </Link>
              <div className="home-user-info">
                <div className="home-user-name">{user.display_name}</div>
                <div className="home-user-meta">@{user.username} · {user.role}</div>
              </div>
              <button className="home-logout" onClick={async () => { await logout(); navigate('/', { replace: true }); }}>sign out</button>
            </>
          ) : (
            <Link to="/login" className="home-signin">sign in  →</Link>
          )}
        </div>
      </header>

      {/* ── B. Wordmark + chicken (no search bar) ── */}
      <div className="hr-brand-row">
        <div className="hr-nacho" aria-hidden="true">
          <img src="/nacho.png" alt="" />
        </div>
        <div className="hr-wordmark">
          <img src="/nachofiles-wordmark.png" alt="NachoFiles" width="373" height="125" />
        </div>
      </div>

      {/* ── C. Quotes chyron ── */}
      {quotes.length > 0 && (
        <div className="hr-chyron">
          {/* Quotes are duplicated so the -50% keyframe loops seamlessly. */}
          <div className="hr-chyron-track">
            {[...quotes, ...quotes].map((q, i) => (
              <span className="hr-chyron-item" key={i}>
                <span className="hr-chyron-quote">“{q.body}”</span>
                {q.attribution && <span className="hr-chyron-attr"> — {q.attribution}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── D. Two-column main row ── */}
      <section className="hr-main">
        {/* Left: three equal-height brown cards (desktop). Mobile also stacks
            the Everything is Stupid card and the compact Starship card here. */}
        <div className="hr-col-left">
          {/* Weather — first on desktop and mobile */}
          <WeatherCard />

          {/* Tracks — static TBTT logo (section branding), never the track art */}
          <Link to="/tracks" className="hr-card">
            <img className="hr-tbtt-logo" src="/tbtt.png" alt="Timmy B’s Tasty Tracks" />
            <div className="hr-card-body">
              <span className="hr-card-label">Now Playing</span>
              <div className="hr-card-title">
                {currentTrack ? currentTrack.title : 'Timmy B’s Tasty Tracks'}
              </div>
              <div className="hr-card-sub">
                {currentTrack ? currentTrack.artist : 'First pick dropping soon'}
              </div>
              {currentTrack?.note && (
                <div className="hr-card-note">{currentTrack.note}</div>
              )}
            </div>
          </Link>

          {/* Funny Pages */}
          <Link to="/funny" className="hr-card">
            <div className="hr-card-media hr-card-media--square">
              {currentComic
                ? <img src={currentComic.thumb_path || currentComic.file_path} alt="" />
                : <span aria-hidden="true">🐔</span>}
            </div>
            <div className="hr-card-body">
              <span className="hr-card-label">New</span>
              <div className="hr-card-title">
                {currentComic ? currentComic.title : 'The Funny Page'}
              </div>
              <div className="hr-card-sub">
                {currentComic
                  ? (currentComic.description || 'The Funny Page')
                  : 'Comics for chicken enthusiasts'}
              </div>
            </div>
          </Link>

          {/* Everything is Stupid (static) — mobile-only copy. On desktop it
              lives in the banner grid below as a banner-shaped tile. */}
          <div className="hr-card hr-card--stupid hr-stupid--mobile">
            <span className="hr-comingsoon">Coming Soon...</span>
            <img
              className="hr-stupid-img"
              src="/images/homepage/everything-is-stupid.png"
              alt="Everything is Stupid"
            />
          </div>

          {/* Mobile-only: compact Starship card. The full photo panel (the
              right column) is hidden ≤768px; this stacks as the 4th card and
              taps through to the full-screen /launches page. */}
          <Link to="/launches" className="hr-card hr-card--launch">
            <div className="hr-card-body">
              <span className="hr-card-label">Next Launch</span>
              <div className="hr-card-title">
                {starship ? starship.name : 'Starship'}
              </div>
              {starship?.location && (
                <div className="hr-card-sub">{starship.location}</div>
              )}
              {starship && (
                <div className="hr-ss-countdown hr-ss-countdown--mini">
                  <CountdownClock cd={cd} />
                </div>
              )}
            </div>
          </Link>
        </div>

        {/* Right: Starship launch panel */}
        <div className="hr-col-right">
          <div className="hr-starship">
            <div className="hr-starship-overlay" />
            <div className="hr-starship-inner">
              {/* Mission hero — hidden entirely when there's no Starship flight */}
              {starship && (
                <div className="hr-ss-hero">
                  <div className="hr-ss-name">{starship.name}</div>
                  {starship.location && <div className="hr-ss-loc">{starship.location}</div>}
                  <div className="hr-ss-countdown">
                    <CountdownClock cd={cd} />
                  </div>
                </div>
              )}

              {/* Equal spacers center the upcoming list vertically */}
              <div className="hr-ss-spacer" />

              <div className="hr-ss-upcoming">
                <div className="hr-ss-upcoming-label">Upcoming Launches</div>
                <ul className="hr-ss-list">
                  {upcoming.map((l, i) => (
                    <li className="hr-ss-launch" key={i}>
                      <span className="hr-ss-launch-name">{l.name}</span>
                      <span className="hr-ss-launch-date">{shortDate(l.net)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="hr-ss-spacer" />
            </div>
          </div>
        </div>
      </section>

      {/* ── E. Banners ──
          Desktop: 2×2 grid, cells placed explicitly in CSS (Stupid | Money /
          Photos | Jams). Mobile: a single column in DOM order — Photos,
          Money, Jams (the Stupid tile is desktop-only). */}
      <section className="hr-banners">
        <div className="hr-card hr-card--stupid hr-stupid--banner">
          <span className="hr-comingsoon">Coming Soon...</span>
          <img
            className="hr-stupid-img"
            src="/images/homepage/everything-is-stupid.png"
            alt="Everything is Stupid"
          />
        </div>
        <a href="https://photos.nachofiles.com" className="hr-banner hr-banner--photos" target="_blank" rel="noopener">
          <img src="/images/homepage/nacho-photos-banner.png" alt="NachoPhotos" />
        </a>
        <a href="https://money.nachofiles.com" className="hr-banner hr-banner--money" target="_blank" rel="noopener">
          <img src="/images/homepage/nacho-money-banner.png" alt="NachoMoney" />
        </a>
        <a href="https://jams.nachofiles.com" className="hr-banner hr-banner--jams" target="_blank" rel="noopener">
          <img src="/images/homepage/jams-banner.jpg" alt="NachoJams" />
        </a>
      </section>

      {/* ── F. Utilities — juggling chicken + app icons ── */}
      <section className="hr-utils">
        <div className="hr-utils-col">
          {UTILS_LEFT.map((u) => <UtilIcon key={u.label} {...u} />)}
        </div>

        <div className="hr-utils-chicken">
          <img src="/images/homepage/nacho-juggle-apps.png" alt="" aria-hidden="true" />
        </div>

        <div className="hr-utils-col">
          {UTILS_RIGHT.map((u) => <UtilIcon key={u.label} {...u} />)}
          {/* NachoMovies — uncomment when frontend is ready
          <a href="https://movies.nachofiles.com" className="hr-util" target="_blank" rel="noopener">
            <span className="hr-util-icon">
              <img src="/images/homepage/icon-nachomovies.png" alt="" />
            </span>
            <span className="hr-util-label">NachoMovies</span>
          </a>
          */}
        </div>
      </section>

      {/* ── G. Footer ── */}
      <footer className="hr-footer">
        <img className="hr-footer-avatar" src="/images/homepage/claude-avatar.jpg" alt="" />
        <div className="hr-footer-row">
          <div>© 2026 nachofiles · self-hosted with questionable judgment</div>
          <div className="hr-footer-tagline">the reason the chicken crossed the road</div>
        </div>
      </footer>
    </div>
  );
}


function UtilIcon({ label, url, icon }) {
  return (
    <a href={url} className="hr-util" target="_blank" rel="noopener">
      <span className="hr-util-icon">
        <img src={`/images/homepage/${icon}`} alt="" />
      </span>
      <span className="hr-util-label">{label}</span>
    </a>
  );
}
